import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Loader2, AlertTriangle, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchBillingSubscription,
  fetchAvailableAddons,
  fetchInvoices,
  fetchPaymentMethods,
  cancelSubscription,
  reactivateSubscription,
  cancelScheduledDowngrade,
  switchBillingCycle,
  setPlanAutoRenew,
  cancelCatalogAddon,
  setAddonAutoRenew,
  fetchBillingActivity,
} from '../../lib/customerBillingApi';
import { INTERNAL_TO_SLUG, isCatalogAddonActive, getCatalogAddonStatus } from '../../lib/pricingUtils';
import CancelSubscriptionDialog from '../billing/customer/CancelSubscriptionDialog';
import BillingHistorySection from '../billing/customer/BillingHistorySection';
import TranslationLocalizationSettings from './TranslationLocalizationSettings';

const STATUS_BADGE = {
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  trialing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  past_due: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  canceled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  canceling: 'bg-amber-100 text-amber-800',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function BillingSettings({ orgId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [addons, setAddons] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [billingActivity, setBillingActivity] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const planSlug = INTERNAL_TO_SLUG[sub?.plan_id];
  const isGrowingOrScaling = planSlug === 'growing' || planSlug === 'scaling';

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [s, inv, pm, hist] = await Promise.all([
        fetchBillingSubscription(orgId),
        fetchInvoices(orgId).catch(() => ({ items: [] })),
        fetchPaymentMethods(orgId).catch(() => ({ items: [] })),
        fetchBillingActivity(orgId).catch(() => ({ activity: [], payments: [] })),
      ]);
      setSub(s);
      setInvoices(inv.items || []);
      setPaymentMethods(pm.items || []);
      setBillingActivity(hist.activity || []);
      setPaymentHistory(hist.payments || []);
      if (INTERNAL_TO_SLUG[s.plan_id] === 'growing' || INTERNAL_TO_SLUG[s.plan_id] === 'scaling') {
        const av = await fetchAvailableAddons(orgId).catch(() => ({ addons: [] }));
        setAddons(av.addons || []);
      } else {
        setAddons([]);
      }
    } catch {
      toast.error('Failed to load billing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [orgId]);

  const planAutoRenewOn = sub?.auto_renew_enabled !== false;

  const activeAddonRows = () => {
    const catalog = sub?.catalog_addons || [];
    const ids = catalog.length
      ? catalog.filter((a) => a.active).map((a) => a.addon_id)
      : ['ai_assistant', 'global_search', 'branding_control', 'extra_team_members', 'extra_custom_domains'].filter(
          (id) => isCatalogAddonActive(sub, id)
        );
    const names = {
      ai_assistant: 'AI Assistant',
      global_search: 'Global Search',
      advanced_search: 'Advanced Search',
      branding_control: 'Branding Control',
      insights_analytics: 'Insights & Analytics',
      faq_widget: 'FAQ Widget',
      translation_localization: 'Translation & Localization',
      extra_team_members: 'Extra team members',
      extra_custom_domains: 'Extra custom domains',
    };
    return ids.map((id) => {
      const status = getCatalogAddonStatus(sub, id);
      let qty = 1;
      let price = null;
      if (id === 'extra_team_members') {
        qty = sub?.purchased_extra_member_slots || 0;
        price = sub?.extra_member_price_usd_monthly;
      } else if (id === 'extra_custom_domains') {
        qty = sub?.purchased_extra_domain_slots || 0;
        price = sub?.extra_domain_price_usd_monthly;
      } else if (id === 'faq_widget') {
        qty = status?.purchased_quantity ?? sub?.faqs_addon?.purchased_widgets ?? 0;
        price = sub?.faqs_addon?.base_price_usd;
      } else if (id === 'translation_localization') {
        qty = status?.purchased_quantity ?? sub?.translation_addon?.purchased_languages ?? 0;
        price = sub?.translation_addon?.base_price_usd;
      } else if (id === 'ai_assistant') {
        price = sub?.ai_addon?.price_usd;
      } else if (id === 'global_search') {
        price = sub?.search_addon?.list_usd_monthly;
      } else if (id === 'branding_control') {
        price = sub?.insights_addon?.list_usd_monthly;
      }
      return { id, name: names[id] || id, qty, price, status };
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-16 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  const statusKey = sub?.cancel_at_period_end ? 'canceling' : sub?.status || 'active';

  return (
    <div className="space-y-8" data-testid="billing-settings">
      {sub?.pending_plan_id && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/30">
          <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-900 dark:text-amber-100">
                Downgrade to {sub.pending_plan_name || sub.pending_plan_id} scheduled for{' '}
                {fmtDate(sub.scheduled_change_at || sub.current_period_end)}.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await cancelScheduledDowngrade(orgId);
                  toast.success('Downgrade cancelled');
                  await load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Cancel downgrade
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Section 1 — Current plan */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 flex-wrap">
                {sub?.plan_name || 'Free trial'}
                <Badge className={STATUS_BADGE[statusKey] || STATUS_BADGE.active}>
                  {statusKey === 'canceling' ? 'Canceling' : sub?.status || 'Active'}
                </Badge>
                <Badge variant="outline">{sub?.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}</Badge>
              </CardTitle>
              <CardDescription className="mt-2">
                Next renewal: {fmtDate(sub?.current_period_end)}
                {sub?.price_usd > 0 && ` · ~$${sub.price_usd}/${sub?.billing_cycle === 'yearly' ? 'yr' : 'mo'}`}
              </CardDescription>
              {sub?.plan_id && sub.plan_id !== 'free' && (
                <div className="flex items-center gap-2 mt-3">
                  <Label htmlFor="plan-auto-renew" className="text-sm">
                    Auto-renew plan
                  </Label>
                  <Switch
                    id="plan-auto-renew"
                    checked={sub.auto_renew_enabled !== false}
                    disabled={busy}
                    onCheckedChange={async (v) => {
                      setBusy(true);
                      try {
                        await setPlanAutoRenew(orgId, v);
                        toast.success(v ? 'Auto-renew enabled' : 'Auto-renew disabled');
                        await load();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed');
                      } finally {
                        setBusy(false);
                      }
                    }}
                  />
                </div>
              )}
            </div>
            <Button variant="outline" onClick={() => navigate('/dashboard/pricing')}>
              Change plan
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Section 2 — Add-ons */}
      {isGrowingOrScaling && (
        <Card>
          <CardHeader>
            <CardTitle>Add-ons</CardTitle>
            <CardDescription>Active add-ons on your workspace</CardDescription>
          </CardHeader>
          <CardContent>
            {activeAddonRows().length === 0 ? (
              <p className="text-sm text-slate-500">No add-ons active.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-500">
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Qty</th>
                      <th className="py-2 pr-4">Price</th>
                      <th className="py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeAddonRows().map((row) => (
                      <tr key={row.id} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-3 pr-4 font-medium">{row.name}</td>
                        <td className="py-3 pr-4">{row.qty}</td>
                        <td className="py-3 pr-4">${row.price ?? '—'}/mo</td>
                        <td className="py-3">
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              <Label htmlFor={`ar-${row.id}`} className="text-xs">
                                Auto-renew
                              </Label>
                              <Switch
                                id={`ar-${row.id}`}
                                checked={row.status?.auto_renew_enabled !== false}
                                disabled={busy || !planAutoRenewOn}
                                onCheckedChange={async (v) => {
                                  if (v && !planAutoRenewOn) {
                                    toast.error('Enable plan auto-renew first');
                                    return;
                                  }
                                  setBusy(true);
                                  try {
                                    await setAddonAutoRenew(orgId, row.id, v);
                                    toast.success(v ? 'Auto-renew enabled' : 'Auto-renew disabled');
                                    await load();
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Failed');
                                  } finally {
                                    setBusy(false);
                                  }
                                }}
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 w-fit"
                              disabled={row.status?.cancel_at_period_end}
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    `Cancel ${row.name} at period end? No refund for current period.`
                                  )
                                ) {
                                  return;
                                }
                                setBusy(true);
                                try {
                                  await cancelCatalogAddon(orgId, row.id);
                                  toast.success('Add-on scheduled for removal');
                                  await load();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Failed');
                                } finally {
                                  setBusy(false);
                                }
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Button className="mt-4" variant="outline" onClick={() => navigate('/dashboard/pricing')}>
              Add add-on
            </Button>
          </CardContent>
        </Card>
      )}

      <TranslationLocalizationSettings orgId={orgId} subscription={sub} onUpdated={load} />

      {/* Section 3 — Billing cycle */}
      {sub?.plan_id && sub.plan_id !== 'free' && (
        <Card>
          <CardHeader>
            <CardTitle>Billing cycle</CardTitle>
            <CardDescription>
              Currently billed {sub.billing_cycle === 'yearly' ? 'yearly' : 'monthly'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sub.billing_cycle === 'monthly' ? (
              <Button
                variant="outline"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await switchBillingCycle(orgId, 'yearly');
                    toast.success('Switched to yearly billing');
                    await load();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Switch to Yearly (save on annual billing)
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await switchBillingCycle(orgId, 'monthly');
                    toast.info('Switch to monthly will apply at period end where applicable');
                    await load();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Switch to Monthly
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Section 4 — Payment methods */}
      <Card>
        <CardHeader>
          <CardTitle>Payment methods</CardTitle>
          <CardDescription>Saved cards for renewals</CardDescription>
        </CardHeader>
        <CardContent>
          {paymentMethods.length === 0 ? (
            <p className="text-sm text-slate-500">
              No saved cards yet. Pay via SkipCash checkout to add a payment method.
            </p>
          ) : (
            <ul className="space-y-2 text-sm">
              {paymentMethods.map((pm) => (
                <li key={pm.id} className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  {pm.brand} ···· {pm.last4}
                  {pm.is_default && <Badge variant="secondary">Default</Badge>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Section 5 — Invoices (Stripe) */}
      {invoices.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Invoice history</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="py-2">Date</th>
                    <th className="py-2">Description</th>
                    <th className="py-2">Amount</th>
                    <th className="py-2">Status</th>
                    <th className="py-2">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b">
                      <td className="py-2">{fmtDate(inv.created_at || inv.date)}</td>
                      <td className="py-2">{inv.description || inv.number}</td>
                      <td className="py-2">${((inv.amount_cents || inv.amount || 0) / 100).toFixed(2)}</td>
                      <td className="py-2">{inv.status}</td>
                      <td className="py-2">
                        {inv.invoice_url || inv.hosted_invoice_url ? (
                          <a
                            href={inv.invoice_url || inv.hosted_invoice_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 underline"
                          >
                            PDF
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <BillingHistorySection payments={paymentHistory} activity={billingActivity} />

      {/* Section 6 — Danger zone */}
      {sub?.plan_id && sub.plan_id !== 'free' && (
        <Card className="border-red-200 dark:border-red-900">
          <CardHeader>
            <CardTitle className="text-red-700 dark:text-red-400">Danger zone</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            {sub.cancel_at_period_end ? (
              <Button
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await reactivateSubscription(orgId);
                    toast.success('Subscription reactivated');
                    await load();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Reactivate subscription
              </Button>
            ) : (
              <Button variant="destructive" onClick={() => setCancelOpen(true)}>
                Cancel subscription
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <CancelSubscriptionDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        periodEnd={sub?.current_period_end}
        isYearly={sub?.billing_cycle === 'yearly'}
        loading={busy}
        onConfirm={async (reason) => {
          setBusy(true);
          try {
            await cancelSubscription(orgId);
            toast.success('Subscription will cancel at period end');
            setCancelOpen(false);
            await load();
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}
